import { isEmpty } from 'lodash-es'
import { AuthorisedUser, DealDetail, SpreadsheetMapping, ValidationIssue } from 'api'
import { Locale, Translation } from 'locale'
import { gatherIssues, parseNumber, parseString, SpreadsheetParseResult } from 'view/shared/upload/model'
import { Nullable } from 'utils'
import { transformHaircutInput } from '../datagrid'

export interface AllocationRawData {
  [key: string]: any
  isin: any
  notional: any
  haircut: any
  allInPrice: any
  startConsideration: any
}

export interface AllocationData {
  isin: string
  notional: Nullable<number>
  haircut: Nullable<number>
  allInPrice: Nullable<number>
  startConsideration: Nullable<number>
}

export const CollateralBasketAllocationFieldNames: { [p in keyof AllocationData]: string } = {
  isin: 'isin',
  notional: 'notional',
  allInPrice: 'allInPrice',
  haircut: 'haircut',
  startConsideration: 'startConsideration'
}

export interface CollateralAllocationParseResult extends SpreadsheetParseResult<AllocationRawData> {
  pendingAllocations: AllocationData[]
}

interface CollateralAllocationSpreadsheetParseContext {
  dealDetail: DealDetail
  authorisedUser: AuthorisedUser
}
export interface CollateralAllocationSpreadsheetParseParams {
  rowData: AllocationRawData[]
  filename: string
  spreadsheetMapping: SpreadsheetMapping
}

interface CollateralAllocationSpreadsheetParseRowResult {
  parseIssues: ValidationIssue[]
  allocation: AllocationData
}

export const parseCollateralAllocationSpreadsheet = (context: CollateralAllocationSpreadsheetParseContext) => ({
  rowData: rows,
  filename,
  spreadsheetMapping
}: CollateralAllocationSpreadsheetParseParams): CollateralAllocationParseResult =>
  rows.reduce<CollateralAllocationParseResult>(
    (r, row, idx) => {
      const { parseIssues, allocation } = parseRow(row, idx + 1, spreadsheetMapping, context)
      return {
        ...r,
        issues: [...r.issues, ...parseIssues],
        pendingAllocations: isEmpty(parseIssues) ? [...r.pendingAllocations, allocation] : r.pendingAllocations
      }
    },
    { filename, rows, issues: [], pendingAllocations: [] }
  )

const parseRow = (
  row: any,
  rowNum: number,
  spreadsheetMapping: SpreadsheetMapping,
  context: CollateralAllocationSpreadsheetParseContext
): CollateralAllocationSpreadsheetParseRowResult => {
  const {
    dealDetail: {
      deal: { direction },
      ownOrder
    },
    authorisedUser: {
      userPreference: { haircutSign }
    }
  } = context

  const isin = parseString(row, rowNum, spreadsheetMapping.isin, Locale.translate(Translation.TC_ISIN), true)
  const startConsideration = parseNumber(
    row,
    rowNum,
    spreadsheetMapping.startConsideration,
    Locale.translate(Translation.TC_START_CONSIDERATION),
    true
  )
  const allInPrice = parseNumber(row, rowNum, spreadsheetMapping.allInPrice, Locale.translate(Translation.TC_ALL_IN_PRICE), false)

  const haircut = parseNumber(row, rowNum, spreadsheetMapping.haircut, Locale.translate(Translation.TC_HAIRCUT), false).map(result =>
    transformHaircutInput(result, haircutSign, ownOrder, direction)
  )

  const notional = parseNumber(row, rowNum, spreadsheetMapping.notional, Locale.translate(Translation.TC_NOTIONAL_SHORT), true)

  return {
    parseIssues: gatherIssues(allInPrice, isin, haircut, notional, startConsideration),
    allocation: {
      isin: isin.isValue ? isin.value : '',
      notional: notional.isValue ? notional.value : null,
      startConsideration: startConsideration.isValue ? startConsideration.value : null,
      allInPrice: allInPrice.isValue ? allInPrice.value : null,
      haircut: haircut.isValue ? haircut.value : null
    }
  }
}
